/**
 * ConceptDocument: the picked idea as one readable page, shared by the client's
 * review page and the team's Concept tab after a pick (Pen 08 · ch4 jcU6R,
 * E4vehq, G9CE2T/f6K3Uf/zimap, static GacQN).
 *
 * Desktop: the reference plate on the left (480 wide for a video, 420 for a
 * still), the idea, hook and Why it works on the right. Tablet: the plate at
 * 300 beside the idea and hook, Why it works under both at full width. Phone:
 * one column, "The reference" head over the plate.
 *
 * Only what the data holds shows: there is no insight field and no stored
 * strategy objective today, so there is no insight card and the objective card
 * carries only how the idea delivers; the evidence is the team's alone.
 */
import { useContext, useRef, useState, type ReactNode } from "react";
import { ArrowUpRight, ChevronDown, Image as ImageIcon, ImagePlus, Volume2, VolumeX } from "lucide-react";
import { MediaCarousel } from "@/components/MediaCarousel";
import { cn } from "@/utils/helpers";
import type { IdeaReference, IdeaSourceType } from "@/types/contentCalendar.type";
import PlatformGlyph, { hasGlyph, platformLabel } from "../../components/PlatformGlyph";
import { posterFrameUrl } from "../videoThumbnails";
import { clientClaim } from "../client-window/ClientMonthView";
import IdeaSlide, { IdeaSlideContext, slideFromConcept } from "./IdeaSlide";
import "./ideas.css";

export interface ConceptDocumentSource {
  label: string;
  url?: string;
  /** The team's sources carry a kind; the client's copy does not. */
  type?: IdeaSourceType;
}

export interface ConceptDocumentData {
  title: string;
  synopsis: string | null;
  /** Every hook; the first is the one the client sees. */
  hooks: string[];
  objectiveClaim: string | null;
  pillarName: string | null;
  durationSeconds: number | null;
  channels: string[];
  reference: IdeaReference | null;
  /** The post's frame, for the "No reference" recess (and a static's reference). */
  aspectRatio: string | null;
  sources?: ConceptDocumentSource[];
  /** A static post: the reference is framed as a still at the post's ratio, never played. */
  still?: boolean;
}

const SOURCE_KIND: Record<IdeaSourceType, string> = {
  trend: "Trend",
  signal: "Signal",
  universe: "Universe",
  web: "Web",
  media: "Media",
  reference: "Reference",
};

const REFERENCE_KIND: Record<NonNullable<IdeaReference["kind"]>, string> = {
  trend: "Trend",
  reference: "Reference",
  media: "Media",
  custom: "Link",
};

/** "9:16" as a CSS aspect-ratio. Unknown shapes fall back to 9:16. */
function ratioOf(value: string | null | undefined): string {
  const match = value?.match(/^(\d+)\s*:\s*(\d+)$/);
  return match ? `${match[1]} / ${match[2]}` : "9 / 16";
}

/** "2 min 30 s", "45 s": the Pen's length line. */
function lengthLabel(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m} min` : `${m} min ${s} s`;
}

/** Where the reference came from, under the plate: "from an Instagram trend". */
function whereLine(reference: IdeaReference): string | null {
  const platform = reference.platform ? platformLabel(reference.platform.toUpperCase()) : "";
  const kind = reference.kind;
  if (kind === "media") return "from the brand's media";
  const noun = kind === "trend" ? "trend" : kind === "custom" ? "link" : kind === "reference" ? "reference" : "post";
  if (!platform) return `from a ${noun}`;
  const article = /^[aeiou]/i.test(platform) ? "an" : "a";
  return `from ${article} ${platform} ${noun}`;
}

/** Media chip / Platform over the plate's top right corner. */
function PlatformChip({ platform }: { platform: string }) {
  const key = platform.toUpperCase();
  if (!hasGlyph(key)) return null;
  return (
    <span className="co-media-chip absolute right-2.5 top-2.5" title={platformLabel(key)}>
      <PlatformGlyph platform={key} className="size-[13px]" />
      <span className="sr-only">{platformLabel(key)}</span>
    </span>
  );
}

/** Text action under the plate: 32 tall, 14 icon, Manrope 500 13 muted. */
function TextAction({
  icon,
  label,
  onPress,
  pressed,
}: {
  icon: ReactNode;
  label: string;
  onPress: () => void;
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={pressed}
      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-pill px-1 text-[13px] font-medium text-co-muted outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
    >
      {icon}
      {label}
    </button>
  );
}

function ReferencePlate({
  reference,
  aspectRatio,
  still: stillOnly = false,
  onChange,
}: {
  reference: IdeaReference | null;
  aspectRatio: string | null;
  still?: boolean;
  onChange?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  if (!reference) {
    return (
      <div className="flex flex-col gap-3">
        <div
          className="grid w-full place-items-center rounded-card bg-co-recess px-6 text-center"
          style={{ aspectRatio: ratioOf(aspectRatio) }}
        >
          <div className="flex flex-col items-center gap-2">
            <ImageIcon className="size-5 text-co-faint" strokeWidth={1.5} aria-hidden="true" />
            <p className="text-[14px] font-medium text-co-text2">No reference</p>
          </div>
        </div>
        {onChange ? (
          <div className="flex items-center justify-end">
            <TextAction
              icon={<ImagePlus className="size-3.5" strokeWidth={1.8} aria-hidden="true" />}
              label="Choose"
              onPress={onChange}
            />
          </div>
        ) : null}
      </div>
    );
  }

  const slides = reference.items ?? [];
  const isVideo = !stillOnly && slides.length <= 1 && !!reference.videoUrl && reference.mediaType !== "image";
  const still = reference.imageUrl ?? reference.thumbnailUrl ?? null;
  // A static with only a video reference still shows that video's first frame.
  const posterOnly = stillOnly && slides.length <= 1 && !still && !!reference.videoUrl;
  const kind = reference.kind ? REFERENCE_KIND[reference.kind] : null;
  const caption = [kind, reference.label?.trim() || null].filter(Boolean).join(" · ");
  const where = whereLine(reference);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => setPlaying(false));
    else video.pause();
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative w-full overflow-hidden rounded-card bg-[var(--co-ink,#121212)]"
        style={{ aspectRatio: ratioOf(stillOnly ? (aspectRatio ?? "4:5") : reference.type) }}
      >
        {slides.length > 1 ? (
          <MediaCarousel items={slides} label={reference.label} fit="cover" className="absolute inset-0" />
        ) : isVideo ? (
          <>
            <video
              ref={videoRef}
              // A #t= fragment paints a frame before anyone presses play.
              src={reference.videoUrl ? posterFrameUrl(reference.videoUrl) : undefined}
              poster={still ?? undefined}
              muted={muted}
              playsInline
              loop
              preload="metadata"
              aria-label={reference.label}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              className="absolute inset-0 size-full object-cover"
            />
            {/* The whole plate plays and pauses; the Pen draws no play mark. */}
            <button
              type="button"
              onClick={toggle}
              aria-label={playing ? "Pause the reference" : "Play the reference"}
              className="absolute inset-0 cursor-pointer outline-none focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white"
            />
          </>
        ) : still ? (
          <img src={still} alt={reference.label} className="absolute inset-0 size-full object-cover" />
        ) : posterOnly && reference.videoUrl ? (
          <video
            src={posterFrameUrl(reference.videoUrl)}
            muted
            playsInline
            preload="metadata"
            aria-label={reference.label}
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <ImageIcon className="size-6 text-white/50" strokeWidth={1.4} aria-hidden="true" />
          </div>
        )}

        <PlatformChip platform={reference.platform} />

        {caption && slides.length <= 1 ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-card bg-gradient-to-b from-black/0 to-black/85 px-3.5 pb-3.5 pt-[72px]">
            <p className="truncate text-[13px] font-semibold text-white">{caption}</p>
          </div>
        ) : null}
      </div>

      {where || isVideo || onChange ? (
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-co-muted">
            {where ? (
              <>
                <span className="hidden @[1000px]/cdoc:inline">The reference · </span>
                <span className="@[1000px]/cdoc:hidden">{where.charAt(0).toUpperCase() + where.slice(1)}</span>
                <span className="hidden @[1000px]/cdoc:inline">{where}</span>
              </>
            ) : null}
          </p>
          {isVideo ? (
            <TextAction
              icon={
                muted ? (
                  <VolumeX className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                ) : (
                  <Volume2 className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                )
              }
              label="Sound"
              pressed={!muted}
              onPress={() => setMuted((value) => !value)}
            />
          ) : null}
          {onChange ? (
            <TextAction
              icon={<ImagePlus className="size-3.5" strokeWidth={1.8} aria-hidden="true" />}
              label="Change"
              onPress={onChange}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Calendar / Concept card: solid surface, radius 12, inside co-line hairline. */
function ConceptCard({ head, aside, children }: { head: string; aside?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-card bg-co-surface px-4 py-3.5 shadow-[inset_0_0_0_1px_var(--co-line)] @[600px]/cdoc:gap-2.5 @[600px]/cdoc:px-[18px] @[600px]/cdoc:pb-[18px] @[600px]/cdoc:pt-4">
      <div className="flex items-center gap-2">
        <p className="text-[12.5px] text-co-text2">{head}</p>
        <span className="flex-1" />
        {aside ? <p className="text-[12.5px] text-co-faint">{aside}</p> : null}
      </div>
      {children}
    </div>
  );
}

/** Calendar / Concept card · Source: the evidence's well, radius 12 on co-recess. */
function SourceWell({ source, alsoReference }: { source: ConceptDocumentSource; alsoReference: boolean }) {
  const body = (
    <>
      <span className="flex items-center gap-2">
        {source.type ? <span className="text-[12.5px] text-co-accent-ink">{SOURCE_KIND[source.type]}</span> : null}
        <span className="flex-1" />
        {source.url ? <ArrowUpRight className="size-3.5 text-co-muted" strokeWidth={1.8} aria-hidden="true" /> : null}
      </span>
      <span className="text-[15px] font-semibold leading-[1.35] text-co-text">{source.label}</span>
      {alsoReference ? <span className="text-[12.5px] text-co-muted">Also the reference</span> : null}
    </>
  );
  const shell =
    "flex flex-col gap-1.5 rounded-card bg-co-recess px-3 py-2.5 @[600px]/cdoc:gap-2 @[600px]/cdoc:p-3";
  return source.url ? (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      className={cn(
        shell,
        "unstyled-link outline-none transition-colors hover:bg-[var(--co-tonal)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
      )}
    >
      {body}
    </a>
  ) : (
    <div className={shell}>{body}</div>
  );
}

export default function ConceptDocument({
  data,
  audience,
  brandName,
  pillarHue,
  onChangeReference,
  className,
}: {
  data: ConceptDocumentData;
  audience: "team" | "client";
  /** "{brand} sees this hook". */
  brandName: string;
  /** The pillar's colour, as a CSS value; none draws a neutral rule. */
  pillarHue?: string | null;
  /** The team's Change under the reference (opens the media picker). */
  onChangeReference?: () => void;
  className?: string;
}) {
  const slideContext = useContext(IdeaSlideContext);
  const client = audience === "client";
  const hook = data.hooks[0]?.trim() || null;
  const otherHooks = client ? [] : data.hooks.slice(1).filter((entry) => entry.trim());
  const claim = data.objectiveClaim?.trim() ? (client ? clientClaim(data.objectiveClaim) : data.objectiveClaim.trim()) : null;
  const length = lengthLabel(data.durationSeconds);
  const channels = data.channels.filter(hasGlyph);
  const sources = client ? [] : (data.sources ?? []);
  const meta = [data.pillarName?.trim() || null, length].filter(Boolean) as string[];
  const referenceLabel = data.reference?.label?.trim().toLowerCase() ?? "";
  const cards = (claim ? 1 : 0) + (sources.length ? 1 : 0);

  const idea = (
    <header
      className="flex flex-col gap-2.5 border-l-[3px] pl-3.5 @[600px]/cdoc:gap-3 @[600px]/cdoc:py-0.5 @[600px]/cdoc:pl-5"
      style={{ borderLeftColor: pillarHue ?? "var(--co-line2)" }}
    >
      <h2 className="font-section text-[26px] font-bold leading-[1.12] tracking-normal text-co-text @[600px]/cdoc:text-[40px] @[600px]/cdoc:leading-[1.1]">
        {data.title}
      </h2>
      {data.synopsis?.trim() ? (
        <p dir="auto" className="text-[15.5px] leading-[1.5] text-co-text2 @[600px]/cdoc:text-[18px]">
          {data.synopsis}
        </p>
      ) : null}
      {meta.length || channels.length ? (
        <div className="flex flex-wrap items-center gap-2.5 @[600px]/cdoc:gap-3 @[600px]/cdoc:pt-0.5">
          {meta.length ? <p className="text-[13px] text-co-text2">{meta.join(" · ")}</p> : null}
          {channels.length ? (
            <span className="inline-flex items-center gap-2.5 text-co-muted @[600px]/cdoc:gap-2">
              {channels.map((channel) => (
                <span key={channel} className="inline-flex">
                  <PlatformGlyph platform={channel} className="size-3.5" />
                  <span className="sr-only">{platformLabel(channel)}</span>
                </span>
              ))}
            </span>
          ) : null}
        </div>
      ) : null}
    </header>
  );

  const hookCard = hook ? (
    <div className="flex flex-col">
      <figure className="flex flex-col gap-2 rounded-card bg-co-accent-dim px-4 py-3.5 @[600px]/cdoc:gap-2.5 @[600px]/cdoc:px-6 @[600px]/cdoc:py-[22px]">
        <blockquote dir="auto" className="text-[17px] font-semibold leading-[1.4] text-co-text @[600px]/cdoc:text-[26px] @[600px]/cdoc:leading-[1.35]">
          “{hook}”
        </blockquote>
        <figcaption className="text-[12.5px] text-co-accent-ink">{brandName} sees this hook</figcaption>
      </figure>
      {otherHooks.length ? (
        <details className="group mt-3">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[13px] text-co-muted hover:text-co-text">
            <ChevronDown className="size-3.5 transition-transform group-open:rotate-180" strokeWidth={2} aria-hidden="true" />
            {otherHooks.length} more {otherHooks.length === 1 ? "hook" : "hooks"}
          </summary>
          <ul className="mt-2 flex flex-col gap-2 border-l border-co-line pl-4">
            {otherHooks.map((entry, index) => (
              <li key={`${index}-${entry}`} dir="auto" className="text-[15px] leading-[1.45] text-co-text2">
                “{entry}”
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  ) : null;

  const why = cards ? (
    <section className="flex flex-col gap-3" aria-label="Why it works">
      <p className="text-[12.5px] text-co-text2">Why it works</p>
      <div
        className={cn(
          "grid gap-3",
          // Three columns in the Pen (insight, objective, evidence); the insight
          // has no field yet, so the cards that exist share the row.
          cards === 2 ? "@[600px]/cdoc:grid-cols-2" : "@[600px]/cdoc:grid-cols-1",
        )}
      >
        {claim ? (
          <ConceptCard head="The objective">
            <p className="text-[12.5px] text-co-accent-ink">How this idea delivers</p>
            <p dir="auto" className="text-[13.5px] leading-[1.45] text-co-text2">
              {claim}
            </p>
          </ConceptCard>
        ) : null}
        {sources.length ? (
          <ConceptCard head={`Sources · ${sources.length}`}>
            <div className="flex flex-col gap-2">
              {sources.map((source, index) => (
                <SourceWell
                  key={`${index}-${source.label}`}
                  source={source}
                  alsoReference={!!referenceLabel && referenceLabel === source.label.trim().toLowerCase()}
                />
              ))}
            </div>
          </ConceptCard>
        ) : null}
      </div>
    </section>
  ) : null;

  const plate = (
    <ReferencePlate
      reference={data.reference}
      aspectRatio={data.aspectRatio}
      still={data.still}
      onChange={client ? undefined : onChangeReference}
    />
  );

  const stacked = (
    <div className={cn("@container/cdoc w-full min-w-0", className)}>
      {/* One tree, three placements. Phone: the reference under its head, then
          the idea and hook, then Why it works. Tablet: the plate at 300 beside
          the idea and hook, Why it works under both. Desktop: the plate at 480
          (420 for a still) on the left, everything else beside it. */}
      <div
        className={cn(
          "grid grid-cols-1 gap-4 [grid-template-areas:'ref'_'main'_'why']",
          "@[600px]/cdoc:grid-cols-[300px_minmax(0,1fr)] @[600px]/cdoc:gap-x-6 @[600px]/cdoc:gap-y-7 @[600px]/cdoc:[grid-template-areas:'ref_main'_'why_why']",
          "@[1000px]/cdoc:grid-rows-[auto_1fr] @[1000px]/cdoc:gap-x-10 @[1000px]/cdoc:gap-y-[26px]",
          // The client's page reads the idea first and keeps the reference beside
          // it on the right (Pen r9LhBH); the team's puts the plate first.
          audience === "client"
            ? "@[760px]/cdoc:grid-rows-[auto_1fr] @[760px]/cdoc:gap-x-10 @[760px]/cdoc:grid-cols-[minmax(0,1fr)_360px] @[760px]/cdoc:[grid-template-areas:'main_ref'_'why_ref']"
            : cn(
                "@[1000px]/cdoc:[grid-template-areas:'ref_main'_'ref_why']",
                data.still ? "@[1000px]/cdoc:grid-cols-[420px_minmax(0,1fr)]" : "@[1000px]/cdoc:grid-cols-[480px_minmax(0,1fr)]",
              ),
        )}
      >
        <aside className="flex min-w-0 flex-col gap-4 self-start [grid-area:ref]" aria-label="The reference">
          <p className={cn("text-[12.5px] text-co-text2", audience === "client" ? "@[600px]/cdoc:hidden @[760px]/cdoc:block" : "@[600px]/cdoc:hidden")}>
            The reference
          </p>
          {plate}
        </aside>
        <div className="flex min-w-0 flex-col gap-4 [grid-area:main] @[600px]/cdoc:gap-6 @[1000px]/cdoc:gap-[26px]">
          {idea}
          {hookCard}
        </div>
        {why ? <div className="min-w-0 pt-2 [grid-area:why] @[600px]/cdoc:pt-0">{why}</div> : null}
      </div>
    </div>
  );
  // Figma 08: the concept as the slide (the idea slide, without the strip) on a
  // wide page; the stacked document stays for the phone.
  if (!slideContext) return stacked;
  return (
    <div className="@container/cslide w-full min-w-0">
      <div className="hidden max-w-[1160px] @[720px]/cslide:block">
        <IdeaSlide
          data={slideFromConcept(data, { ...slideContext, pillarHue: pillarHue ?? slideContext.pillarHue })}
          onChangeMedia={onChangeReference}
        />
      </div>
      <div className="@[720px]/cslide:hidden">{stacked}</div>
    </div>
  );
}
